import { PanelRiskRow } from "../../types/geneCodes/Panel";
import { calculateNumericRiskScore, NumericRiskCounts } from "./riskScoringUtils";

export interface MiniWheelSlice {
	trait: string;
	value: number;
	riskLevel: "good" | "medium" | "bad" | "ungenotyped" | null;
}

export interface TraitBreakdown {
	trait: string;
	high: number;
	med: number;
	no: number;
	noCall: number;
	notGenotyped: number;
}

// staging.category joins Panel.name_panel as a raw string (legacy SQL had to TRIM it), so normalise both sides.
export const panelKey = (name: string): string => name.trim().toLowerCase();

interface GeneCounts {
	high: number;
	med: number;
	no: number;
	noCall: number;
	notGenotyped: number;
}

interface TraitAggregate {
	trait: string;
	rank: number | null;
	genes: GeneCounts[];
	high: number;
	med: number;
	no: number;
	noCall: number;
	notGenotyped: number;
}

const groupRows = (rows: PanelRiskRow[]): Map<string, Map<string, TraitAggregate>> => {
	const panels = new Map<string, Map<string, TraitAggregate>>();

	rows.forEach((row) => {
		const key = panelKey(row.panel);
		let traits = panels.get(key);
		if (!traits) {
			traits = new Map<string, TraitAggregate>();
			panels.set(key, traits);
		}

		let trait = traits.get(row.trait);
		if (!trait) {
			trait = { trait: row.trait, rank: null, genes: [], high: 0, med: 0, no: 0, noCall: 0, notGenotyped: 0 };
			traits.set(row.trait, trait);
		}

		if (row.rank !== null && (trait.rank === null || row.rank < trait.rank)) {
			trait.rank = row.rank;
		}

		// Absent on a payload from a labiq that predates the partition fix, which is a zero here rather than a NaN.
		const noCall = row.no_call ?? 0;
		const notGenotyped = row.not_genotyped ?? 0;

		trait.genes.push({ high: row.high_risk, med: row.med_risk, no: row.no_risk, noCall, notGenotyped });
		trait.high += row.high_risk;
		trait.med += row.med_risk;
		trait.no += row.no_risk;
		trait.noCall += noCall;
		trait.notGenotyped += notGenotyped;
	});

	return panels;
};

// A no-call always counts; the never-measured markers count for an author and not for a patient, hence the default.
const traitValue = (trait: TraitAggregate, hideUngenotyped: boolean = false): number => {
	const snps = trait.genes.reduce((total, gene) => total + gene.high + gene.med + gene.no + gene.noCall + (hideUngenotyped ? 0 : gene.notGenotyped), 0);
	// The Sunburst doubles a single-SNP trait so its arc stays visible.
	return snps === 1 ? 2 : snps;
};

const traitRiskLevel = (trait: TraitAggregate, hideUngenotyped: boolean = false): MiniWheelSlice["riskLevel"] => {
	const counts: NumericRiskCounts = { total: 0, sum: 0, count: 0 };

	trait.genes.forEach((gene) => {
		const snpCount = gene.high + gene.med + gene.no;
		if (snpCount === 0) return;

		// 3 = homozygous risk, 2 = heterozygous, 1 = no risk; the trait averages per gene, not per SNP.
		const geneSum = 3 * gene.high + 2 * gene.med + gene.no;
		counts.sum += geneSum / snpCount;
		counts.count += 1;
		counts.total += 1;
	});

	// Scored over called SNPs only: an absent result must not average a trait towards green.
	// Slate IS the modal saying "not genotyped", just as a colour, so a patient shell takes the no-call grey instead.
	if (counts.count === 0) return !hideUngenotyped && trait.notGenotyped > 0 ? "ungenotyped" : null;

	return calculateNumericRiskScore(counts, true);
};

const compareTraits = (a: TraitAggregate, b: TraitAggregate): number => {
	if (a.rank === null && b.rank === null) return a.trait.localeCompare(b.trait);
	if (a.rank === null) return 1;
	if (b.rank === null) return -1;
	if (a.rank !== b.rank) return a.rank - b.rank;
	return a.trait.localeCompare(b.trait);
};

const orderedTraits = (traits: Map<string, TraitAggregate>, hideUngenotyped: boolean = false): TraitAggregate[] =>
	Array.from(traits.values())
		.filter((trait) => traitValue(trait, hideUngenotyped) > 0)
		.sort(compareTraits);

export const buildPanelMiniWheels = (rows: PanelRiskRow[], hideUngenotyped: boolean = false): Map<string, MiniWheelSlice[]> => {
	const wheels = new Map<string, MiniWheelSlice[]>();

	groupRows(rows).forEach((traits, key) => {
		wheels.set(
			key,
			orderedTraits(traits, hideUngenotyped).map((trait) => ({
				trait: trait.trait,
				value: traitValue(trait, hideUngenotyped),
				riskLevel: traitRiskLevel(trait, hideUngenotyped),
			}))
		);
	});

	return wheels;
};

// Counts stay true here even when hiding: the flag governs which traits survive, PanelList decides what it prints.
export const buildTraitBreakdown = (rows: PanelRiskRow[], panelName: string, hideUngenotyped: boolean = false): TraitBreakdown[] => {
	const traits = groupRows(rows).get(panelKey(panelName));
	if (!traits) return [];

	return orderedTraits(traits, hideUngenotyped).map((trait) => ({
		trait: trait.trait,
		high: trait.high,
		med: trait.med,
		no: trait.no,
		noCall: trait.noCall,
		notGenotyped: trait.notGenotyped,
	}));
};
